<script setup>
import axios from 'axios'
import * as gmaps from '@googlemaps/js-api-loader'
import { ref, onMounted } from 'vue'
import Spinner from '@components/Spinner.vue'
import { generateRecaptchaToken } from '@helpers/utils'

const { Loader } = gmaps

const locationNotFound = ref(false)
const loading = ref(false)
const address = ref('')
const radius = ref(10)
const results = ref([])
const markers = ref([])
const google = ref(null)
const map = ref(null)
const mapEl = ref(null)
const prevInfoWindow = ref(null)

const defaultCoordinates = [30.176673212855448, -97.82053315951865]

onMounted(() => {
  const loader = new Loader({
    apiKey: import.meta.env.PUBLIC_GOOGLE_API_KEY,
    version: 'weekly',
    libraries: ['geometry'],
  })

  loader.load().then((g) => {
    google.value = g

    window.registerLazyLoad(() => {
      loadMap(...defaultCoordinates)
      loadMarkers()
    })

    if (navigator.geolocation) {
      navigator.permissions.query({ name: 'geolocation' })
          .then(permissionStatus => {
            if (permissionStatus.state !== 'granted') {
              searchLocationsNear(...defaultCoordinates)
            }
          })

      navigator.geolocation.getCurrentPosition(
          (position) => {
            loadMap(position.coords.latitude, position.coords.longitude)
            searchLocationsNear(position.coords.latitude, position.coords.longitude)

            createMarker(
                new google.value.maps.LatLng(position.coords.latitude, position.coords.longitude),
                'You are here.',
                'H',
            )
          }
      )
    } else {
      searchLocationsNear(...defaultCoordinates)
    }
  })
})

function search() {
  loading.value = true
  locationNotFound.value = false
  new google.value.maps.Geocoder().geocode(
      { address: address.value },
      async (results, status) => {
        if (status === google.value.maps.GeocoderStatus.OK) {
          const location = results[0].geometry.location
          await searchLocationsNear(location.lat(), location.lng())
        } else {
          locationNotFound.value = true
          loading.value = false
        }
      }
  )
}

function loadMap(latitude, longitude) {
  if (map.value) {
    return
  }

  map.value = new google.value.maps.Map(mapEl.value, {
    zoom: 12,
    minZoom: 11,
    center: {
      lat: latitude,
      lng: longitude,
    },
    mapTypeId: google.value.maps.MapTypeId.ROADMAP,
  })

  map.value.addListener('dragend', async () => {
    const center = map.value.getCenter();

    fetchData(center.lat(), center.lng()).then((data) => {
      data.forEach((store) => {
        createMarker(
          new google.value.maps.LatLng(store.latitude, store.longitude),
          `<strong>${store.name}</strong> <br>${store.address}<br>${store.distance.toFixed(1)} mi.`
        )
      })
    })
  })
}

async function fetchData(latitude, longitude) {
  const token = await generateRecaptchaToken('map')

  return axios.get(
    `https://crm.hometownheroerp.com/map?lat=${latitude}&lng=${longitude}&radius=${radius.value}&captcha=${token}`
  ).then((data) => data.data)
}

function loadMarkers() {
  markers.value.forEach((marker) => {
    marker.setMap(null)
  })
  markers.value = []

  if (results.value.length) {
    results.value.forEach((store) => {
      createMarker(
          new google.value.maps.LatLng(store.latitude, store.longitude),
        `<strong>${store.name}</strong> <br>${store.address}<br>${store.distance.toFixed(1)} mi.`
      )
    })
  }
}

async function searchLocationsNear(latitude, longitude) {
  loading.value = true

  results.value = await fetchData(latitude, longitude).finally(() => loading.value = false)

  if (map.value) {
    map.value.panTo({ lat: latitude, lng: longitude })
    loadMarkers()
  }
}

function createMarker(latLng, content, label = null) {
  const marker = new google.value.maps.Marker({
    map: map.value,
    position: latLng,
    label,
  })

  marker.addListener('click', () => {
    let infoWindow = new google.value.maps.InfoWindow({ content })

    if (prevInfoWindow.value) {
      prevInfoWindow.value.close()
    }
    prevInfoWindow.value = infoWindow

    infoWindow.open({
      anchor: marker,
      map: map.value,
    })
  })

  if (!label) {
    markers.value.push(marker)
  }
}
</script>

<template>
  <form class="locator__form" @submit.prevent="search">
    <div v-if="locationNotFound" class="alert alert--error">
      Location not found. Please try a new search.
    </div>

    <input v-model="address" type="text" class="input" placeholder="Search" required>
    <div class="locator__grid">
      <select v-model="radius" class="select" required>
        <option :value="10">Within 10 mi</option>
        <option :value="25">Within 25 mi</option>
        <option :value="50">Within 50 mi</option>
      </select>
      <button type="submit" class="locator__button">
        <span v-if="!loading">Search</span>
        <Spinner color="white" v-else />
      </button>
    </div>
    <div class="locator__list">
      <div class="locator__result" v-for="(result, i) in results" :key="i">
        <strong>{{ result.name }}</strong> &ndash; {{ result.distance.toFixed(1) }} mi<br />
        <a :href="`https://www.google.com/maps/dir/?api=1&destination=${encodeURI(result.address)}`" target="_blank">
          {{ result.address }}
        </a>
      </div>
    </div>
  </form>
  <div ref="mapEl" class="locator__map">
    <div class="text-center">
      <Spinner />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.locator__form {
  @media screen and (min-width: 768px) {
    flex: 1;
  }

  .input {
    width: 100%;
    margin-bottom: 4px;
  }
  .locator__grid {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 32px;

    @media screen and (min-width: 768px) {
      flex-direction: row;
      margin-bottom: 4px;

      .select {
        flex-grow: 1;
      }
    }

    .locator__button {
      border-radius: 2px;
      background: var(--primary-600, #183D4F);
      box-shadow: 0 4px 6px -4px rgba(16, 24, 40, 0.10), 0px 10px 15px -3px rgba(16, 24, 40, 0.10);
      color: var(--shades-white, #FFF);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px; /* 125% */
      text-transform: uppercase;
      padding: 12px 0;
      cursor: pointer;

      @media screen and (min-width: 768px) {
        font-size: 20px;
        letter-spacing: 1px;
        padding: 16px 36px;
      }
    }
  }

  .locator__list {
    overflow-y: auto;
    max-height: 440px;
    padding: 0 16px;
    border-radius: 2px;
    border: 1px solid var(--neutral-300, #C4C3C1);
    background: var(--shades-white, #FFF);

    @media screen and (min-width: 768px) {
      max-height: 340px;
    }

    .locator__result {
      padding: 16px 0;
      font-size: 16px;
      line-height: 28px;
      border-bottom: 1px solid #C4C3C1;

      a {
        font-weight: 400;
      }
    }
  }
}

.locator__map {
  width: 100%;
  height: 440px;
  border-radius: 8px;

  @media screen and (min-width: 768px) {
    flex: 1;
    height: auto;
  }

  .locator__map-allow {
    display: block;
    border: 1px solid #C4C3C1;
    border-radius: 4px;
    padding: 12px;
    text-align: center;
  }
}
</style>
